import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, apiGet, apiPost, apiPut, apiDelete, setToken, getToken } from "@/lib/api";
import { GRANTS as LOCAL_GRANTS, TAX_CREDITS, SETTLEMENTS, UNCLAIMED as LOCAL_UNCLAIMED } from "@/lib/mockData";

const AppContext = createContext(null);

const EMPTY_PROFILE = {
    legal_first_name: "",
    legal_last_name: "",
    state: "",
    categories: [],
    goals: [],
    completed_onboarding: false,
};

// Map onboarding UI flag key -> profile boolean field.
const FLAG_KEY = {
    veteran: "is_veteran",
    disabled: "is_disabled",
    minority: "is_minority",
    womanOwned: "is_woman_owned",
    firstTimeBuyer: "is_first_time_homebuyer",
    student: "currently_enrolled",
    entrepreneur: "has_business",
    reentry: "is_formerly_incarcerated",
};

export function AppProvider({ children }) {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(EMPTY_PROFILE);
    const [applications, setApplications] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread] = useState(0);
    const [unclaimed, setUnclaimed] = useState([]);
    const [unclaimedTotal, setUnclaimedTotal] = useState(0);
    const [vaultDocs, setVaultDocs] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [booting, setBooting] = useState(true);

    const isAuthed = !!user;

    const refreshProfile = useCallback(async () => {
        try { setProfile(await apiGet("/profile")); } catch (e) { console.debug("refreshProfile skipped:", e?.response?.status); }
    }, []);

    const refreshApplications = useCallback(async () => {
        try { setApplications(await apiGet("/applications")); } catch (e) { console.debug("refreshApplications skipped:", e?.response?.status); }
    }, []);

    const refreshNotifications = useCallback(async () => {
        try {
            const d = await apiGet("/notifications");
            setNotifications(d.items || []);
            setUnread(d.unread || 0);
        } catch (e) { console.debug("refreshNotifications skipped:", e?.response?.status); }
    }, []);

    const refreshUnclaimed = useCallback(async (state) => {
        try {
            const d = await apiGet("/unclaimed/search" + (state ? `?state=${encodeURIComponent(state)}` : ""));
            setUnclaimed(d.results || []);
            setUnclaimedTotal(d.total_estimate || 0);
        } catch (e) { console.debug("refreshUnclaimed skipped:", e?.response?.status); }
    }, []);

    const refreshVaultDocs = useCallback(async () => {
        try { const d = await apiGet("/vault/documents"); setVaultDocs(d.items || []); } catch (e) { console.debug("refreshVaultDocs skipped:", e?.response?.status); }
    }, []);

    const refreshAdmin = useCallback(async () => {
        try { const d = await apiGet("/admin/whoami"); setIsAdmin(!!d.is_admin); } catch { setIsAdmin(false); }
    }, []);

    const bootstrap = useCallback(async () => {
        const t = getToken();
        if (!t) { setBooting(false); return; }
        try {
            const me = await apiGet("/auth/me");
            setUser(me);
            await Promise.all([refreshProfile(), refreshApplications(), refreshNotifications(), refreshUnclaimed(), refreshVaultDocs(), refreshAdmin()]);
        } catch {
            setToken(null);
            setUser(null);
        } finally {
            setBooting(false);
        }
    }, [refreshProfile, refreshApplications, refreshNotifications, refreshUnclaimed, refreshVaultDocs, refreshAdmin]);

    useEffect(() => { bootstrap(); }, [bootstrap]);

    // Wipes every piece of cached user state. Called from logout AND at the
    // very start of every login/signup so a prior user's data can never bleed
    // into the next session even for a frame.
    const _clearAllUserState = useCallback(() => {
        setUser(null);
        setProfile(EMPTY_PROFILE);
        setApplications([]);
        setNotifications([]);
        setUnread(0);
        setUnclaimed([]);
        setUnclaimedTotal(0);
        setVaultDocs([]);
        setIsAdmin(false);
    }, []);

    // Auth
    const signup = async ({ name, email, password }) => {
        // Hard-reset any previous session state BEFORE swapping tokens.
        setToken(null);
        _clearAllUserState();
        const d = await apiPost("/auth/signup", { name, email, password });
        setToken(d.token);
        setUser(d.user);
        await Promise.all([refreshProfile(), refreshApplications(), refreshNotifications(), refreshUnclaimed(), refreshVaultDocs(), refreshAdmin()]);
        return d.user;
    };
    const login = async ({ email, password }) => {
        // Hard-reset any previous session state BEFORE swapping tokens.
        setToken(null);
        _clearAllUserState();
        const d = await apiPost("/auth/login", { email, password });
        setToken(d.token);
        setUser(d.user);
        await Promise.all([refreshProfile(), refreshApplications(), refreshNotifications(), refreshUnclaimed(), refreshVaultDocs(), refreshAdmin()]);
        return d.user;
    };
    const logout = () => {
        setToken(null);
        _clearAllUserState();
    };

    // Profile
    const updateProfile = async (patch) => {
        const d = await apiPut("/profile", patch);
        setProfile(d);
        return d;
    };
    const toggleFlag = async (uiKey) => {
        const key = FLAG_KEY[uiKey] || uiKey;
        const next = !profile?.[key];
        await updateProfile({ [key]: next });
    };
    const toggleCategory = async (id) => {
        const current = profile.categories || [];
        const next = current.includes(id) ? current.filter(c => c !== id) : [...current, id];
        await updateProfile({ categories: next });
    };
    const toggleGoal = async (id) => {
        const current = profile.goals || [];
        const next = current.includes(id) ? current.filter(g => g !== id) : [...current, id];
        await updateProfile({ goals: next });
    };

    // Applications
    const startApplication = async ({ type, target_id, target_title, target_amount, target_meta }) => {
        const d = await apiPost("/applications", { type, target_id, target_title, target_amount: target_amount || 0, target_meta });
        await Promise.all([refreshApplications(), refreshNotifications()]);
        return d;
    };
    const getApplication = async (id) => apiGet(`/applications/${id}`);
    const prepareApplication = async (id) => {
        const d = await apiPost(`/applications/${id}/prepare`);
        await Promise.all([refreshApplications(), refreshNotifications()]);
        return d;
    };
    const answerQuestion = async (appId, questionId, answer) => {
        const d = await apiPost(`/applications/${appId}/answer`, { question_id: questionId, answer });
        await refreshApplications();
        return d;
    };
    const signApplication = async (appId, { signature_data_url, typed_name }) => {
        const d = await apiPost(`/applications/${appId}/sign`, { signature_data_url, typed_name, consent: true });
        await Promise.all([refreshApplications(), refreshNotifications()]);
        return d;
    };
    const fileApplication = async (appId, { method = "portal", portal_url = null, delivery_email = null, notes = null } = {}) => {
        const d = await apiPost(`/applications/${appId}/file`, { method, portal_url, delivery_email, notes });
        await Promise.all([refreshApplications(), refreshNotifications()]);
        return d;
    };
    const updateAppStatus = async (appId, { status, note = null, agency_reference = null }) => {
        const d = await apiPost(`/applications/${appId}/status`, { status, note, agency_reference });
        await Promise.all([refreshApplications(), refreshNotifications()]);
        return d;
    };
    const getAppTimeline = async (appId) => apiGet(`/applications/${appId}/timeline`);
    const deleteApplication = async (id) => {
        await apiDelete(`/applications/${id}`);
        await refreshApplications();
    };
    const downloadPacket = async (id, filename = "owed-packet.pdf") => {
        try {
            const r = await api.get(`/applications/${id}/packet`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (err) {
            // axios with responseType:blob serves error as a Blob too — unpack it to JSON/text
            const blob = err?.response?.data;
            let detail = "Couldn't download packet";
            if (blob instanceof Blob) {
                try {
                    const text = await blob.text();
                    try { const j = JSON.parse(text); detail = j.detail || j.message || text; }
                    catch { detail = text || detail; }
                } catch { /* keep fallback */ }
            } else if (err?.message) { detail = err.message; }
            throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
        }
    };
    const findApplicationByTarget = (targetId) => applications.find((a) => a.target_id === targetId);

    // Notifications
    const markReadAll = async () => { await apiPost("/notifications/read-all"); await refreshNotifications(); };
    const markRead = async (ids) => { await apiPost("/notifications/read", { ids }); await refreshNotifications(); };

    // Derived: scored curated grants (the beautiful hand-picked catalog)
    const scoredGrants = useMemo(() => {
        const userState = (profile?.state || "").toUpperCase();
        return LOCAL_GRANTS.map((g) => {
            let score = g.matchScore;
            if (profile?.is_veteran && g.category === "veteran") score = Math.min(99, score + 5);
            if (profile?.is_woman_owned && g.tags?.includes("women-owned")) score = Math.min(99, score + 6);
            if (profile?.is_minority && g.tags?.includes("minority-owned")) score = Math.min(99, score + 5);
            if (profile?.is_first_time_homebuyer && g.tags?.includes("first-time buyer")) score = Math.min(99, score + 8);
            if (profile?.is_formerly_incarcerated && g.category === "reentry") score = Math.min(99, score + 10);
            if ((profile?.categories || []).includes(g.category)) score = Math.min(99, score + 4);
            // State-restricted programs: big boost for residents, big drop for everyone else.
            if (g.tags?.includes("state-only") || g.tags?.includes("local")) {
                const grantState = (g.tags || []).find(t => t.length === 2 && /^[a-z]{2}$/i.test(t))?.toUpperCase()
                    || (g.tags?.includes("texas") ? "TX" : null);
                if (grantState) {
                    if (grantState === userState) score = Math.min(99, score + 6);
                    else score = Math.max(0, score - 35); // don't surface TX-only programs to non-TX users
                }
            }
            return { ...g, matchScore: score };
        }).sort((a, b) => b.matchScore - a.matchScore);
    }, [profile]);

    const totals = useMemo(() => {
        const unclaimedVal = unclaimed.length ? unclaimedTotal : LOCAL_UNCLAIMED.reduce((s, u) => s + u.amount, 0);
        const creditsTotal = TAX_CREDITS.reduce((s, t) => s + t.estimate, 0);
        const settlementsTotal = SETTLEMENTS.filter((s) => s.status === "Open").reduce((s, x) => s + x.estimate, 0);
        const topGrantsTotal = scoredGrants.slice(0, 6).reduce((s, g) => s + g.amount, 0);
        return {
            unclaimed: unclaimedVal,
            credits: creditsTotal,
            settlements: settlementsTotal,
            grants: topGrantsTotal,
            potential: unclaimedVal + creditsTotal + settlementsTotal + topGrantsTotal,
        };
    }, [scoredGrants, unclaimed, unclaimedTotal]);

    // Used by /reset-password to drop the brand-new session into the context
    // without reloading. Mirrors what login()/signup() do, minus the API call.
    const setSession = useCallback(async (auth) => {
        if (!auth?.token) return;
        _clearAllUserState();
        setToken(auth.token);
        setUser(auth.user);
        await Promise.all([refreshProfile(), refreshApplications(), refreshNotifications(), refreshUnclaimed(), refreshVaultDocs(), refreshAdmin()]);
    }, [_clearAllUserState, refreshProfile, refreshApplications, refreshNotifications, refreshUnclaimed, refreshVaultDocs, refreshAdmin]);

    const value = {
        user, isAuthed, booting,
        profile, refreshProfile, updateProfile, toggleFlag, toggleCategory, toggleGoal,
        signup, login, logout, setSession,
        applications, refreshApplications, startApplication, getApplication,
        prepareApplication, answerQuestion, signApplication, fileApplication, updateAppStatus, getAppTimeline,
        deleteApplication, downloadPacket,
        findApplicationByTarget,
        notifications, unread, refreshNotifications, markReadAll, markRead,
        unclaimed, unclaimedTotal, refreshUnclaimed,
        vaultDocs, refreshVaultDocs,
        isAdmin,
        scoredGrants, totals,
    };
    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used within AppProvider");
    return ctx;
};
